import React, { useEffect, useRef, useState } from "react"
import { Badge, Button } from "reactstrap"
import Participant from "../../components/ParticipantSummary"
import { withChildPlace, withEntryDetails } from "../../components/participantDisplay"
import { entryUser } from "components/ReviewAudit/PublicRatingScope"
import { seedRankOf } from "../../components/seeds"
import DrawConnections from "../Matches/components/DrawConnections"
import "./draw.scss"
import { drawLockReason } from "./drawFeedbackCopy"

const identity = (row, groups) => `${groups ? row.group : row.stage}:${row.position}`
const positionName = (row, groups) => groups
  ? `Group ${String.fromCharCode(64 + row.group)} · Position ${row.position}`
  : `${row.stage === 0 ? "Qualifications" : "First stage"} · Position ${row.position}`
const personName = person => typeof person === "string" ? person : [person?.firstName, person?.lastName].filter(Boolean).join(" ") || person?.type || "Participant unavailable"
// A Tenis10 child's slot holds its parent's account snapshot on `user`: the person is the child, as its card shows it
const entryName = row => [entryUser(row.slot), row.slot?.partner].filter(Boolean).map(personName).join(" / ")
const placeholderOf = row => typeof row.slot?.user === "string" ? row.slot.user : row.slot?.user?.type
// The Main Draw pairs eliminatory positions 1-2, 3-4, … into matches; the Lucky Draw shows the same pairs.
const matchNumber = row => Math.ceil(row.position / 2)
export const eliminatoryMatches = rows => [...rows].sort((a, b) => a.position - b.position)
  .reduce((matches, row) => {
    const last = matches[matches.length - 1]
    if (last && last.number === matchNumber(row)) last.rows.push(row)
    else matches.push({ number: matchNumber(row), rows: [row] })
    return matches
  }, [])
const stageName = stage => stage === 0 ? "Qualifications" : "First stage"
const matchName = (stage, number) => `${stage === 0 ? "Qualifying" : "Match"} ${number}`
// A qualifying pair feeds one first-stage position; the pending placeholder there names that pair.
const qualifyingFor = (luckyDraw, position) => {
  const feeder = luckyDraw.find(row => row.stage === 0 && row.nextStagePosition === position)
  return feeder ? matchNumber(feeder) : null
}
const feedPosition = rows => rows.map(row => row.nextStagePosition).find(value => value != null) ?? null
// Each qualifying pair takes the bracket row of the first-stage match its winner joins, so it is
// listed level with that match; two pairs feeding the same match share its row, and a pair feeding
// no listed match gets a row of its own after the last match.
export const bracketRows = (qualifying, firstStage) => {
  const rowOfMatch = new Map(firstStage.map((block, index) => [block.number, index]))
  const cells = new Map()
  let count = firstStage.length
  qualifying.forEach((block, index) => {
    const destination = feedPosition(block.rows)
    const match = destination != null ? rowOfMatch.get(matchNumber({ position: destination })) : undefined
    const row = match ?? count++
    cells.set(row, [...(cells.get(row) || []), { block, index }])
  })
  return { count, qualifying: [...cells].sort((a, b) => a[0] - b[0]).map(([row, blocks]) => ({ row, blocks })) }
}
const matchNote = (stage, rows) => {
  if (stage === 0) {
    const destination = feedPosition(rows)
    return destination != null ? `Winner plays First stage · Position ${destination}` : null
  }
  if (rows.some(row => placeholderOf(row) === "BYE")) return "Advances without playing"
  return null
}

// `readOnly` shows a phase draw: positions cannot be selected, because a swap always changes the competition's own Lucky Draw.
export default function DrawPositions({ luckyDraw, drawModel, gameType, currentCompetition, allCompetitions, eventEntryCounts,
  eventId, competitionId, phaseId, operation, eventContext, readOnly = false }) {
  const groups = drawModel === "groups"
  const lockReason = readOnly ? undefined : drawLockReason(operation)
  const helpId = `draw-positions-${competitionId}-${phaseId || "primary"}-help`
  const [selection, setSelection] = useState([])
  const confirmationRef = useRef(null)
  useEffect(() => { if (selection.length === 2) confirmationRef.current?.focus() }, [selection])
  const revision = JSON.stringify(luckyDraw)
  useEffect(() => { setSelection([]) }, [eventId, competitionId, revision])
  const choose = row => setSelection(previous => previous.some(item => identity(item, groups) === identity(row, groups)) ? [] : previous.length === 1 ? [...previous, row] : [row])
  // Eliminatory draws use the Main Draw layout: a Qualifications column and a First stage
  // column side by side, each listed top to bottom, with connection lines drawn from every
  // qualifying pair to the first-stage match whose pending position its winner takes.
  const qualifying = groups ? [] : eliminatoryMatches(luckyDraw.filter(row => row.stage === 0))
  const firstStage = groups ? [] : eliminatoryMatches(luckyDraw.filter(row => row.stage === 1))
  const rows = bracketRows(qualifying, firstStage)
  const canvas = useRef(null)
  const connectionSections = [
    { qualification: true, rows: qualifying.map(block => ({ competition: { position: block.number, nextStagePosition: feedPosition(block.rows) } })) },
    { rows: firstStage.map(block => ({ competition: { position: block.number } })) },
  ]
  const sections = [...new Set(luckyDraw.map(row => row.group))].sort((a, b) => a - b)
  const eventDetails = { ...eventContext, entryCountsByPlayerId: eventEntryCounts, competitions: allCompetitions }
  const invalidSwap = !groups && selection.length === 2 && selection.some((row, index) =>
    (row.slot?.user === "PENDING" || row.slot?.user?.type === "PENDING") && selection[1 - index].stage === 0)
  useEffect(() => { if (operation.status === "saved") setSelection([]) }, [operation.status])
  const swap = () => {
    if (readOnly || invalidSwap || operation.locked) return
    const [source, target] = selection
    operation.run("swap", { lastPosition: source.position, position: target.position,
      ...(groups ? { lastGroup: source.group, group: target.group } : { lastStage: source.stage, stage: target.stage }) })
  }
  return <div className="lucky-draw-positions" role="group" aria-label="Player positions"
    data-locked={Boolean(lockReason)} tabIndex={lockReason ? 0 : undefined} title={lockReason}
    aria-describedby={helpId} onKeyDown={event => { if (event.key === "Escape") setSelection([]) }}>
    <p id={helpId} className="text-muted">{readOnly ? "Phase draws are generated from Phases." : lockReason || "Select two positions to exchange their players or teams."}</p>
    {selection.length === 1 && <div role="status" className="lucky-draw-selection">Selected: {positionName(selection[0], groups)} — {entryName(selection[0])}. Select the other position.
      <Button color="link" onClick={() => setSelection([])}>Cancel selection</Button>
    </div>}
    {selection.length === 2 && <section aria-label="Confirm position swap" className="lucky-draw-selection" tabIndex={-1} ref={confirmationRef}>
      <p>{positionName(selection[0], groups)}: {entryName(selection[0])}</p>
      <p>{positionName(selection[1], groups)}: {entryName(selection[1])}</p>
      {invalidSwap && <p role="alert">A qualifier placeholder cannot be moved into Qualifications.</p>}
      <Button color="primary" aria-describedby={lockReason ? helpId : undefined} disabled={invalidSwap || operation.locked} onClick={swap}>Swap</Button>{" "}<Button color="secondary" disabled={operation.locked} onClick={() => setSelection([])}>Cancel</Button>
    </section>}
    {groups ? <div className="lucky-draw-sections">
      {sections.map(section => <section key={section} aria-label={`Group ${String.fromCharCode(64 + section)}`}>
        <h3>{`Group ${String.fromCharCode(64 + section)}`}</h3>
        {luckyDraw.filter(row => row.group === section).sort((a, b) => a.position - b.position).map(position)}
      </section>)}
    </div> : <div className="lucky-draw-bracket" ref={canvas} style={{ "--lucky-draw-rows": rows.count }}>
      {qualifying.length > 0 && <section aria-label={stageName(0)} className="lucky-draw-column lucky-draw-column--qualifications">
        <h3>{stageName(0)}</h3>
        {rows.qualifying.map(({ row, blocks }) => <div key={row} className="lucky-draw-cell" style={{ gridRow: row + 2 }}>
          {blocks.map(({ block, index }) => <div key={block.number} className="lucky-draw-slot" data-bracket-position={`0:${index}`}>{matchBlock(0, block)}</div>)}
        </div>)}
      </section>}
      <section aria-label={stageName(1)} className="lucky-draw-column">
        <h3>{stageName(1)}</h3>
        {firstStage.map((block, index) => <div key={block.number} className="lucky-draw-cell" style={{ gridRow: index + 2 }}>
          <div className="lucky-draw-slot" data-bracket-position={`${qualifying.length ? 1 : 0}:${index}`}>{matchBlock(1, block)}</div>
        </div>)}
      </section>
      {qualifying.length > 0 && <DrawConnections canvas={canvas} sections={connectionSections} />}
    </div>}
  </div>
  function position(row) {
    const selected = selection.some(item => identity(item, groups) === identity(row, groups))
    return <article key={identity(row, groups)} className={`lucky-draw-position ${selected ? "is-selected" : ""}`}>
      {readOnly
        ? <div className="lucky-draw-position-name">{positionName(row, groups)}</div>
        : <Button color="light" disabled={operation.locked} aria-describedby={lockReason ? helpId : undefined} aria-label={`Select ${positionName(row, groups)}`} aria-pressed={selected} onClick={() => choose(row)}>{positionName(row, groups)}</Button>}
      <DrawEntry row={row} groups={groups} gameType={gameType} competition={currentCompetition} eventDetails={eventDetails}
        qualifying={!groups && row.stage === 1 ? qualifyingFor(luckyDraw, row.position) : null} />
    </article>
  }
  function matchBlock(stage, { number, rows: sides }) {
    const note = matchNote(stage, sides)
    return <section key={number} className="lucky-draw-match" aria-label={matchName(stage, number)}>
      <header className="lucky-draw-match-heading"><strong>{matchName(stage, number)}</strong>{note && <span className="text-muted">{note}</span>}</header>
      <div className="lucky-draw-match-sides">{sides.map(position)}</div>
    </section>
  }
}

function DrawEntry({ row, groups, gameType, competition, eventDetails, qualifying = null }) {
  const entry = row.slot || {}
  const current = [...(competition?.mainList || []), ...(competition?.waitingList || [])].find(item =>
    entry._id ? String(item._id) === String(entry._id) : (entry.user?.userId != null &&
      item.user?.userId === entry.user.userId && item.partner?.userId === entry.partner?.userId))
  const seedRank = seedRankOf(row, entry, competition)
  const placeholder = placeholderOf(row)
  if (["BYE", "PENDING"].includes(placeholder)) return <div className="lucky-draw-placeholder">
    {placeholder === "PENDING" ? `Qualifier pending${qualifying != null ? ` · Winner of Qualifying ${qualifying}` : ""}` : "BYE"}
  </div>
  return <div className="lucky-draw-entry">
    <div className="entries-participants">
      <Participant variant="draw" person={withChildPlace(withEntryDetails(entryUser(entry), current?.user), competition)} seedRank={seedRank} gameType={gameType} eventDetails={eventDetails} />
      {gameType === "doubles" && <Participant variant="draw" person={withEntryDetails(entry.partner, current?.partner)} gameType={gameType} eventDetails={eventDetails} />}
    </div>
    <div className="entries-team-summary">
      {entry.competitionRank != null && <span title="Main List order">ML #{entry.competitionRank}</span>}
      {entry.slotType === "WILD_CARD" && <Badge color="info">WC</Badge>}
    </div>
  </div>
}
